import {
  EmailAuthProvider,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
  sendEmailVerification,
  signOut,
  deleteUser,
} from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { AuthErrorConverter } from "./converters/AuthErrorConverter";
import { AuthUserConverter } from "./converters/AuthUserConverter";
import { AuthUser } from "../../domain/models/AuthUser";
import { CoreErrorCause, CoreError } from "../../domain/models/CoreError";

export class AuthenticationService {
  private static instance: AuthenticationService;

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
    }
    return AuthenticationService.instance;
  }

  public get userId(): string | null {
    return auth.currentUser?.uid ?? null;
  }

  public get isUserLoggedIn(): boolean {
    return !!auth.currentUser;
  }

  public get authUser(): AuthUser | null {
    return auth.currentUser
      ? AuthUserConverter.domainModel(auth.currentUser)
      : null;
  }

  public observeAuthState(onNext: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, (user) =>
      onNext(AuthUserConverter.domainModel(user)),
    );
  }

  public async signUp(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = AuthUserConverter.domainModel(userCredential.user);
      if (user) {
        return user;
      } else {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: "Sign-up failed: User not returned",
        });
      }
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = AuthUserConverter.domainModel(userCredential.user);
      if (user) {
        return user;
      } else {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: "Sign-in failed: User not returned",
        });
      }
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async reauthenticateUser(currentPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: "User doesn't have an email",
        });
      }
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(user, credential);
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async verifyAndUpdateEmail(
    newEmail: string,
    currentPassword: string,
  ): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: "No user to update",
        });
      }
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, newEmail);
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async sendEmailVerification(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: "No user signed in",
        });
      }
      await sendEmailVerification(user);
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      throw AuthErrorConverter.domainModel(error);
    }
  }

  public async deleteAccount(): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      // The backend delete callable may already remove auth user.
      return;
    }

    try {
      await deleteUser(user);
    } catch (error) {
      if (this.isUserAlreadyDeletedError(error)) {
        return;
      }
      throw AuthErrorConverter.domainModel(error);
    }
  }

  private isUserAlreadyDeletedError(error: unknown): boolean {
    if (!error || typeof error !== "object" || !("code" in error)) {
      return false;
    }

    const code = String((error as { code?: unknown }).code ?? "").toLowerCase();
    // The backend delete callable removes the auth user itself, so the
    // follow-up client-side delete sees a dead session: Firebase surfaces
    // that as user-not-found or as a token error when it tries to refresh
    // the ID token of the now-deleted account.
    return (
      code.includes("user-not-found") ||
      code.includes("no-current-user") ||
      code.includes("user-token-expired") ||
      code.includes("invalid-user-token") ||
      code.includes("user-disabled")
    );
  }
}
